// Mini-notation recursive-descent parser.
//
// Strudel's mini-notation is a tiny DSL that lives inside pattern
// strings: `"bd*4 [cp, sn] ~ <g a c>"`. The grammar is:
//
//   pattern    := sequence
//   sequence   := element ( WS+ element )*
//   element    := primary modifier*
//   primary    := atom | rest | '[' tracks ']'
//                     | '<' sequence '>' | '{' tracks '}' ('%' number)?
//   tracks     := sequence ( ',' sequence )*
//   atom       := NAME ( ':' INT )?
//   rest       := '~' | '-'
//   modifier   := '*' number         -- repeat (stretches within cycle)
//              | '/' number         -- slow  (spreads across cycles)
//              | '!' number         -- bang  (repeat w/o stretch)
//              | '@' number         -- weight
//              | '(' number ',' number (',' number)? ')'  -- euclid
//
// Modifiers bind tightly (no whitespace). Whitespace separates elements
// inside a sequence. Commas separate parallel tracks inside brackets
// and braces.
//
// Unrecognised input returns { ok: false } with a position — callers
// fall back to storing the slot as { kind: 'raw', source }.

import type { MiniNode, MiniPattern, Numeric } from '../types';
import { newBlockId } from '../types';

export type MiniParseResult =
  | { ok: true; nodes: MiniPattern }
  | { ok: false; reason: string; position: number };

export function parseMini(source: string): MiniParseResult {
  const p = new Parser(source);
  try {
    const nodes = p.parseSequence(null);
    p.skipWS();
    if (!p.eof()) {
      return {
        ok: false,
        reason: `Unexpected ${JSON.stringify(p.peek())} at position ${p.pos}`,
        position: p.pos,
      };
    }
    return { ok: true, nodes };
  } catch (e) {
    if (e instanceof MiniParseError) {
      return { ok: false, reason: e.message, position: e.pos };
    }
    throw e;
  }
}

class MiniParseError extends Error {
  constructor(message: string, public pos: number) {
    super(message);
  }
}

class Parser {
  pos = 0;

  constructor(public source: string) {}

  eof(): boolean {
    return this.pos >= this.source.length;
  }

  peek(offset = 0): string {
    return this.source[this.pos + offset] ?? '';
  }

  skipWS(): void {
    while (!this.eof() && /\s/.test(this.peek())) this.pos++;
  }

  // Parse a whitespace-separated sequence of elements, stopping at a
  // closing terminator (']', '>', '}'), at a comma (track boundary),
  // or at end of input.
  parseSequence(terminator: string | null): MiniNode[] {
    const nodes: MiniNode[] = [];
    while (true) {
      this.skipWS();
      if (this.eof()) break;
      const c = this.peek();
      if (c === ',') break;
      if (terminator !== null && c === terminator) break;
      nodes.push(this.parseElement());
    }
    return nodes;
  }

  parseElement(): MiniNode {
    let node = this.parsePrimary();
    // Modifier chain — tight binding, no whitespace.
    while (!this.eof()) {
      const c = this.peek();
      if (c === '*') {
        this.pos++;
        const count = this.parseNumeric();
        node = { kind: 'repeat', id: newBlockId(), inner: node, count };
      } else if (c === '/') {
        this.pos++;
        const factor = this.parseNumeric();
        node = { kind: 'slow', id: newBlockId(), inner: node, factor };
      } else if (c === '!') {
        this.pos++;
        const count = this.parseNumeric();
        node = { kind: 'bang', id: newBlockId(), inner: node, count };
      } else if (c === '@') {
        this.pos++;
        const weight = this.parseNumeric();
        node = { kind: 'weight', id: newBlockId(), inner: node, weight };
      } else if (c === '(') {
        this.pos++;
        this.skipWS();
        const pulses = this.parseNumeric();
        this.skipWS();
        this.expect(',');
        this.skipWS();
        const steps = this.parseNumeric();
        this.skipWS();
        let rot: Numeric | undefined;
        if (this.peek() === ',') {
          this.pos++;
          this.skipWS();
          rot = this.parseNumeric();
          this.skipWS();
        }
        this.expect(')');
        node = {
          kind: 'euclid',
          id: newBlockId(),
          inner: node,
          pulses,
          steps,
          ...(rot !== undefined ? { rot } : {}),
        };
      } else {
        break;
      }
    }
    return node;
  }

  parsePrimary(): MiniNode {
    const c = this.peek();
    if (c === '[') return this.parseGroup();
    if (c === '<') return this.parseChoice();
    if (c === '{') return this.parsePolymeter();
    if (c === '~') {
      this.pos++;
      return { kind: 'rest', id: newBlockId() };
    }
    if (c === '-' && this.isStandaloneRest()) {
      this.pos++;
      return { kind: 'rest', id: newBlockId() };
    }
    return this.parseAtom();
  }

  // `-` is a rest only when it stands alone as a token — i.e. the next
  // character is whitespace, EOF, or a structural terminator. Otherwise
  // it could be part of an identifier (we don't currently allow that,
  // but this keeps the door open) or a malformed token we'll report.
  isStandaloneRest(): boolean {
    const next = this.peek(1);
    return next === '' || /\s/.test(next) || ']>},'.includes(next);
  }

  parseAtom(): MiniNode {
    const start = this.pos;
    if (!/[a-zA-Z_]/.test(this.peek())) {
      throw new MiniParseError(
        `Expected atom name, got ${JSON.stringify(this.peek())}`,
        this.pos,
      );
    }
    // Identifiers allow letters, digits, underscore, and `#` for sharps
    // (so `c#4` lexes as one token). Flats use lowercase `b` inside the
    // name (`bb3`, `eb2`) which already falls out of the letter class.
    while (!this.eof() && /[a-zA-Z0-9_#]/.test(this.peek())) this.pos++;
    const name = this.source.slice(start, this.pos);
    let index: number | undefined;
    if (this.peek() === ':' && /[0-9]/.test(this.peek(1))) {
      this.pos++; // consume ':'
      const idxStart = this.pos;
      while (/[0-9]/.test(this.peek())) this.pos++;
      index = Number(this.source.slice(idxStart, this.pos));
    }
    return index === undefined
      ? { kind: 'atom', id: newBlockId(), name }
      : { kind: 'atom', id: newBlockId(), name, index };
  }

  parseGroup(): MiniNode {
    this.expect('[');
    const tracks = this.parseTracks(']');
    this.expect(']');
    if (tracks.length === 1) {
      return { kind: 'group', id: newBlockId(), children: tracks[0]! };
    }
    return {
      kind: 'parallel',
      id: newBlockId(),
      children: tracks.map((t) => wrapTrack(t)),
    };
  }

  parseChoice(): MiniNode {
    this.expect('<');
    const children = this.parseSequence('>');
    this.expect('>');
    return { kind: 'choice', id: newBlockId(), children };
  }

  parsePolymeter(): MiniNode {
    this.expect('{');
    const tracks = this.parseTracks('}');
    this.expect('}');
    let steps: Numeric | undefined;
    if (this.peek() === '%') {
      this.pos++;
      steps = this.parseNumeric();
    }
    const children = tracks.map((t) => wrapTrack(t));
    return {
      kind: 'polymeter',
      id: newBlockId(),
      children,
      ...(steps !== undefined ? { steps } : {}),
    };
  }

  parseTracks(terminator: string): MiniNode[][] {
    const tracks: MiniNode[][] = [this.parseSequence(terminator)];
    while (this.peek() === ',') {
      this.pos++;
      tracks.push(this.parseSequence(terminator));
    }
    return tracks;
  }

  parseNumeric(): Numeric {
    const start = this.pos;
    if (this.peek() === '-') this.pos++;
    if (!/[0-9]/.test(this.peek())) {
      throw new MiniParseError(
        `Expected number at position ${this.pos}`,
        this.pos,
      );
    }
    while (/[0-9]/.test(this.peek())) this.pos++;
    if (this.peek() === '.' && /[0-9]/.test(this.peek(1))) {
      this.pos++;
      while (/[0-9]/.test(this.peek())) this.pos++;
    }
    const value = Number(this.source.slice(start, this.pos));
    return { kind: 'num', value };
  }

  expect(c: string): void {
    if (this.peek() !== c) {
      throw new MiniParseError(
        `Expected ${JSON.stringify(c)}, got ${JSON.stringify(this.peek())} at position ${this.pos}`,
        this.pos,
      );
    }
    this.pos++;
  }
}

// A single-element track unwraps to that element; a multi-element
// track wraps in a `group` so it remains a single MiniNode inside
// a parent's `children`.
function wrapTrack(track: MiniNode[]): MiniNode {
  if (track.length === 1) return track[0]!;
  return { kind: 'group', id: newBlockId(), children: track };
}
